import { OnboardingStepName } from "app/utils/steps"
import { ALLOW_DEV_TOOLS, IS_DESKTOP } from "core/constants"
import { DesktopNotification } from "shared/types/Notification"
import {
  AllExamFiles,
  Base64,
  CipheredData,
  Exam,
  ExamParams,
  LocalMediaFile,
  Logs,
  MimeType,
  StudentPaper,
  User,
} from "types"
import { SSOEstablishment } from "types/Establishment"
import { ExamElapsedTime, ExamTimers, RemainingTimeByExamSection } from "types/ExamTimers"
import { AsyncStorage } from "types/Storage"
import { ContextBridge, IpcRenderer, Shell } from "web/WebElectronTypes"

import { persistentStoresName } from "./constants/peristentStoresName"

import { channels } from "../core/channels"

import type {
  DeviceAuthorization,
  SerializableDesktopCapturerSource,
} from "shared/services/deviceAuthorizations"
import type { DisplayMonitorChangedPayload } from "shared/types/DisplayMonitor.types"

export async function preload({
  contextBridge,
  ipcRenderer,
  shell,
}: {
  contextBridge: ContextBridge
  ipcRenderer: IpcRenderer
  shell: Shell
}) {
  const electronHandler = {
    store: {
      persistStore: (storeName: keyof typeof persistentStoresName): AsyncStorage => {
        return {
          getItem: (key: string): Promise<any> => {
            return ipcRenderer.invoke(`${channels.ELECTRON_PERSIST_STORE_GET}_${storeName}`, key)
          },
          setItem: (key: string, val: any): Promise<void> =>
            ipcRenderer.invoke(`${channels.ELECTRON_PERSIST_STORE_SET}_${storeName}`, key, val),
          removeItem: (key: string): Promise<void> =>
            ipcRenderer.invoke(`${channels.ELECTRON_PERSIST_STORE_REMOVE}_${storeName}`, key),
        }
      },
      fileStore: {
        getItem: <T = any>(storeName: string, userId: User["id"]): T =>
          ipcRenderer.sendSync(channels.GET_FILE_STORE_STATE, userId, storeName) as T,
        setItem: (storeName: string, userId: User["id"], value: any): Promise<void> =>
          ipcRenderer.invoke(channels.SET_FILE_STORE_STATE, userId, storeName, value),
        removeItem: (storeName: string, userId: User["id"]): Promise<void> =>
          ipcRenderer.invoke(channels.CLEAR_FILE_STORE_STATE, userId, storeName),
      },
    },
    shell: {
      openExternal: (url: string, options?: any): Promise<void> => shell.openExternal(url, options),
    },
    log: {
      info: (...args: any[]) => {
        ipcRenderer.send(channels.LOG_INFO, ...args)
      },
      warn: (...args: any[]) => {
        ipcRenderer.send(channels.LOG_WARNING, ...args)
      },
      error: (...args: any[]) => {
        ipcRenderer.send(channels.LOG_ERROR, ...args)
      },
    },
    logs: {
      archiveLogs: (): Promise<void> => {
        return ipcRenderer.invoke(channels.ARCHIVE_LOGS)
      },
      removeOldArchivedLogs: (): Promise<void> => {
        return ipcRenderer.invoke(channels.REMOVE_OLD_ARCHIVED_LOGS)
      },
      getLogs: async (userId: User["id"], examId: Exam["id"]) => {
        const logs: {
          data: Logs | null
          backupPath: string | null
          filename: string | null
        } = await ipcRenderer.invoke(channels.GET_MAIN_LOGS_FILE, userId, examId)

        return {
          logs: logs.data,
          archiveLogs: () =>
            logs.backupPath ? electronHandler.files.removeFile(logs.backupPath) : Promise.resolve(),
          filename: logs.filename,
        }
      },
    },
    sso: {
      login: (
        username: string,
        establishment: SSOEstablishment,
        rememberMe: boolean,
      ): Promise<{ refreshToken: string }> =>
        ipcRenderer.invoke(channels.SSO_LOGIN, username, establishment, rememberMe),
    },
    console: {
      initLog: () => {
        if (!ALLOW_DEV_TOOLS) return
        ipcRenderer.removeAllListeners(channels.CONSOLE_INFO)
        ipcRenderer.on(channels.CONSOLE_INFO, (e, ...args) => {
          // eslint-disable-next-line no-console
          console.log(`[ELECTRON-INFO] ${args.join("\n")}`)
        })
        ipcRenderer.removeAllListeners(channels.CONSOLE_WARNING)
        ipcRenderer.on(channels.CONSOLE_WARNING, (e, ...args) => {
          // eslint-disable-next-line no-console
          console.warn(`[ELECTRON-WARNING] ${args.join(" ")}`)
        })
        ipcRenderer.removeAllListeners(channels.CONSOLE_ERROR)
        ipcRenderer.on(channels.CONSOLE_ERROR, (e, ...args) => {
          console.error(`[ELECTRON-ERROR] ${args.join(" ")}`)
        })
      },
    },
    exam: {
      start: (examParams: Partial<ExamParams> | undefined): Promise<void> =>
        ipcRenderer.invoke(channels.START_EXAM_SECURITY, examParams),
      end: (examParams?: Partial<ExamParams>) =>
        ipcRenderer.invoke(channels.STOP_EXAM_SECURITY, examParams),
      createExamUploadDirectory: (
        user: User,
        exam: Exam,
        studenPaper: StudentPaper | Omit<StudentPaper, "id">,
        localMediaFiles: LocalMediaFile[],
      ): Promise<void> =>
        ipcRenderer.invoke(
          channels.CREATE_EXAM_UPLOAD_DIRECTORY,
          user,
          exam,
          studenPaper,
          localMediaFiles,
        ),
      removeUploadDirectory: (examId: Exam["id"]): Promise<void> =>
        ipcRenderer.invoke(channels.REMOVE_EXAM_UPLOAD_DIRECTORY, examId),
      getUnachivedExamUpload: (examId: Exam["id"]): Promise<AllExamFiles | null> =>
        ipcRenderer.invoke(channels.GET_UNACHIEVED_EXAM_UPLOAD, examId),
      getUnachievedExamIds: (): Promise<Exam["id"][] | null> =>
        ipcRenderer.invoke(channels.GET_UNACHIEVED_EXAM_IDS),
      archiveUploadDirectory: (examId: Exam["id"]): Promise<void> =>
        ipcRenderer.invoke(channels.ARCHIVE_EXAM_UPLOAD_DIRECTORY, examId),
      addOnboardingMediaToUploadExam: (
        examId: Exam["id"],
        data: Base64 | Logs,
        filename: string,
        type: OnboardingStepName | "logs" | "proctorAudio" | "proctorPicture" | "screenRecording",
      ) => {
        return ipcRenderer.invoke(
          channels.ADD_ONBOARDING_MEDIA_TO_UPLOAD_EXAM,
          examId,
          data,
          filename,
          type,
        )
      },
      timers: {
        get: (userId: User["id"], examId: Exam["id"]): ExamTimers | null =>
          ipcRenderer.sendSync(channels.GET_EXAM_TIMERS_STATE, userId, examId),
        getAll: (userId: User["id"]): Promise<Record<string, ExamTimers>> =>
          ipcRenderer.invoke(channels.GET_ALL_EXAM_TIMERS_STATE, userId),
        set: <T extends "remaining" | "elapsed">(
          userId: User["id"],
          examId: Exam["id"],
          type: T,
          timers: T extends "remaining" ? RemainingTimeByExamSection : ExamElapsedTime,
        ): Promise<void> =>
          ipcRenderer.invoke(channels.SET_EXAM_TIMERS_STATE, userId, examId, type, timers),
        clear: (userId: User["id"], examId: Exam["id"]): Promise<void> =>
          ipcRenderer.invoke(channels.CLEAR_EXAM_TIMERS_STATE, userId, examId),
      },
    },
    files: {
      fileExist: (filepath: string): Promise<boolean> =>
        ipcRenderer.invoke<boolean>(channels.FILE_EXIST, filepath),
      removeFile: (filepath: string): Promise<void> =>
        ipcRenderer.invoke(channels.REMOVE_FILE, filepath),
      removeAllExamFiles: (examId: Exam["id"]): Promise<void> =>
        ipcRenderer.invoke(channels.REMOVE_ALL_EXAM_FILES, examId),
      removeEmptyExamDirectories: (): Promise<void> =>
        ipcRenderer.invoke(channels.REMOVE_EMPTY_EXAM_DIRECTORIES),
      readExamFile: async (
        examId: Exam["id"],
        filename: string,
        type: "attached" | "exam",
        decryptKey: string,
      ): Promise<{
        data: Base64 | null
        filePath: string | null
        extension: string | null
        filename: string | null
        path: string | null
        removeFile: () => Promise<void> | null
      }> => {
        const file = await ipcRenderer.invoke(
          channels.READ_EXAM_FILE,
          examId,
          filename,
          type,
          decryptKey,
        )

        return {
          ...file,
          removeFile: () =>
            file?.filePath ? electronHandler.files.removeFile(file.filePath) : null,
        }
      },
      writeExamFile: (
        examId: Exam["id"],
        data: CipheredData,
        filename: string,
        type: "attached" | "exam",
      ): Promise<{
        data: CipheredData
        filePath: string | null
        extension: string | null
        filename: string | null
        path: string | null
      }> => {
        return ipcRenderer.invoke(channels.WRITE_EXAM_FILE, examId, data, filename, type)
      },
      getOptionFromFile: (optionName: string, optionDefaultValue?: boolean): string | boolean => {
        return ipcRenderer.sendSync(channels.GET_OPTION_FROM_FILE, optionName, optionDefaultValue)
      },
      getZippedExamFiles: async (examId: Exam["id"]): Promise<Base64> =>
        ipcRenderer.invoke(channels.GET_UNACHIEVED_EXAM_ARCHIVE, examId),
      fileState: {
        get: <Value = any>(storeFileName: string): Promise<Value> => {
          return ipcRenderer.invoke(channels.GET_FILE_STATE, storeFileName)
        },
        set: (storeFileName: string, state: any): Promise<void> => {
          return ipcRenderer.invoke(channels.SET_FILE_STATE, storeFileName, state)
        },
      },
      medias: {
        removeMediaDirectory: (examId: Exam["id"]): Promise<void> =>
          ipcRenderer.invoke(channels.REMOVE_MEDIA_DIRECTORY, examId),
      },
    },
    update: {
      checkAndInstallUpdate: (channel?: string): Promise<any> =>
        ipcRenderer.invoke(channels.CHECK_AND_INSTALL_UPDATE, channel),
      updateDownloadProgress: (func: (progress: string) => void) => {
        ipcRenderer.on(channels.UPDATE_DOWNLOAD_PROGRESS, (e, progress) => func(progress))
        return () => {
          ipcRenderer.removeAllListeners(channels.UPDATE_DOWNLOAD_PROGRESS)
        }
      },
    },
    informations: {
      getBannerInformations: (): Promise<{
        activeAntivirus: string[]
        availableDiskSpace: number | null
        availableMemory: number | null
      }> => ipcRenderer.invoke(channels.GET_BANNER_INFO),
      getPlatform: (): {
        osPlatform: "macOs" | "Windows" | "Linux" | "Browser" | "unknown"
        platform: "darwin" | "win32" | "linux" | "browser"
      } => ipcRenderer.sendSync(channels.GET_PLATFORM),
      getAppInfos: (): Promise<{
        blockedOs: boolean
        memoryTotal: number
        diskSpaceAvailable: number
      }> => ipcRenderer.invoke(channels.GET_APP_INFOS),
      getAppArchive: async (
        user: User,
        includeSystemDiagnostics = false,
      ): Promise<Uint8Array | null> =>
        ipcRenderer.invoke(channels.GET_ARCHIVE, user, includeSystemDiagnostics),
      checkMemoryUsage: () => ipcRenderer.send(channels.CHECK_MEMORY_USAGE),
    },
    notification: {
      newExam: (notification: DesktopNotification) =>
        ipcRenderer.send(channels.NOTIFY_NEW_EXAM, notification),
      actionHandler: (func: (notification: DesktopNotification) => void) => {
        ipcRenderer.on(channels.NOTIFY_NEW_EXAM_ACTION, (e, notification) => func(notification))
        return () => {
          ipcRenderer.removeAllListeners(channels.NOTIFY_NEW_EXAM_ACTION)
        }
      },
    },
    authorizations: {
      requestDeviceAuthorization: (device: DeviceAuthorization): Promise<boolean> =>
        ipcRenderer.invoke(channels.ASK_MEDIA_OS_PERMISSIONS, device),
      getDeviceAuthorizationStatus: (device: DeviceAuthorization): boolean =>
        ipcRenderer.sendSync(channels.GET_MEDIA_OS_PERMISSIONS, device),
      openSystemPreferences: (device: DeviceAuthorization): Promise<void> =>
        ipcRenderer.invoke(channels.OPEN_DEVICE_SYSTEM_PREFERENCES, device),
    },
    proctoring: {
      handleAudioRecordingChunks: (
        examId: Exam["id"],
        userId: User["id"],
        data: Uint8Array,
        recordingNumber: number,
        mimeType: MimeType,
      ): Promise<void> => {
        return ipcRenderer.invoke(
          channels.PROCTORING_HANDLE_AUDIO_RECORDING_CHUNKS,
          examId,
          userId,
          data,
          recordingNumber,
          mimeType,
        )
      },
      getAudioRecordingFile: async (
        examId: Exam["id"],
        askedRecordingNumber: number,
      ): Promise<{
        file: Base64 | null
        startTimestamp: string | null
        recordingNumber: string | null
        filename: string | null
        removeFile: () => Promise<void>
      }> => {
        const { file, filepath, startTimestamp, recordingNumber, filename } =
          await ipcRenderer.invoke(
            channels.PROCTORING_GET_AUDIO_RECORDING_FILE,
            examId,
            askedRecordingNumber,
          )

        return {
          file,
          filename,
          startTimestamp,
          recordingNumber,
          removeFile: () =>
            filepath ? electronHandler.files.removeFile(filepath) : Promise.resolve(),
        }
      },
      handleScreenRecordingChunks: (
        examId: Exam["id"],
        userId: User["id"],
        data: Uint8Array,
        segmentNumber: number,
        screenId: string,
        mimeType: MimeType,
        startTimestamp: string,
      ): Promise<void> => {
        return ipcRenderer.invoke(
          channels.PROCTORING_HANDLE_SCREEN_RECORDING_CHUNKS,
          examId,
          userId,
          data,
          segmentNumber,
          screenId,
          mimeType,
          startTimestamp,
        )
      },
      getScreenRecordingFile: async (
        examId: Exam["id"],
        segmentNumber: number,
        screenId: string,
      ): Promise<{
        file: Base64 | null
        startTimestamp: string | null
        segmentNumber: string | null
        screenId: string | null
        filename: string | null
        removeFile: () => Promise<void>
      }> => {
        const {
          file,
          filepath,
          startTimestamp,
          segmentNumber: segment,
          screenId: screen,
          filename,
        } = await ipcRenderer.invoke(
          channels.PROCTORING_GET_SCREEN_RECORDING_FILE,
          examId,
          segmentNumber,
          screenId,
        )

        return {
          file,
          filename,
          startTimestamp,
          segmentNumber: segment,
          screenId: screen,
          removeFile: () =>
            filepath ? electronHandler.files.removeFile(filepath) : Promise.resolve(),
        }
      },
      sealScreenRecordingSegment: (
        examId: Exam["id"],
        segmentNumber: number,
        screenId: string,
      ): Promise<void> =>
        ipcRenderer.invoke(
          channels.PROCTORING_SEAL_SCREEN_RECORDING_SEGMENT,
          examId,
          segmentNumber,
          screenId,
        ),
      listPendingScreenSegments: (
        examId: Exam["id"],
      ): Promise<Array<{ segmentNumber: number; screenId: string }>> =>
        ipcRenderer.invoke(channels.PROCTORING_LIST_PENDING_SCREEN_SEGMENTS, examId),
      screen: {
        getDisplayMediaSources: (
          sourceType?: "screen" | "window",
        ): Promise<SerializableDesktopCapturerSource[]> =>
          ipcRenderer.invoke(channels.DISPLAY_MEDIA_GET_SOURCES, { sourceType }),
        setDisplayMediaSource: (source: SerializableDesktopCapturerSource): Promise<boolean> =>
          ipcRenderer.invoke(channels.DISPLAY_MEDIA_SET_SOURCE, { source }),
      },
    },
    app: {
      exit: () => ipcRenderer.send(channels.QUIT_APP),
      restart: () => ipcRenderer.send(channels.RESTART_APP),
      minimize: () => ipcRenderer.send(channels.MINIMIZE_APP),
      maximize: () => ipcRenderer.send(channels.MAXIMIZE_APP),
      toggleFullScreen: (): Promise<boolean> => ipcRenderer.invoke(channels.TOGGLE_FULLSCREEN),
      interfaceLoaded: () => ipcRenderer.send(channels.INTERFACE_LOADED),
      interfaceUnloaded: () => ipcRenderer.send(channels.INTERFACE_UNLOADED),
      getAvailableDiskSpace: (): Promise<number | null> =>
        ipcRenderer.invoke(channels.GET_AVAILABLE_DISK_SPACE),
      getSystemInfos: (): Promise<{
        osVersion: string
        memoryTotal: number
        memoryAvailable: number
        diskSpaceAvailable: number
        proxy: string
        screenNumbers: number
        screenResolutions: string[]
      }> => {
        if (IS_DESKTOP) return ipcRenderer.invoke(channels.GET_EQUIPMENT_INFO)
        else {
          return Promise.resolve({
            osVersion: "browser",
            memoryTotal: 0,
            memoryAvailable: 0,
            diskSpaceAvailable: 0,
            proxy: "",
            screenNumbers: 1,
            screenResolutions: [],
          })
        }
      },
      openPrefPane: (pane?: "datetime" | "camera" | "microphone") =>
        ipcRenderer.send(channels.OPEN_SYSTEM_PREFERENCES, pane),
      onReloadPage: (func: () => void) => {
        ipcRenderer.on(channels.RELOAD_PAGE, func)
        return () => {
          ipcRenderer.removeListener(channels.RELOAD_PAGE, func)
        }
      },
      changeLanguage: (lang: string) => ipcRenderer.send(channels.CHANGE_LANGUAGE, lang),
      onGetCurrentPathname: (func: () => boolean) => {
        const sender = () => {
          ipcRenderer.send(channels.SECURITY_STOP_VALIDATE_ORIGIN_RESPONSE, func())
        }
        ipcRenderer.once(channels.SECURITY_STOP_VALIDATE_ORIGIN, sender)
        return () => {
          ipcRenderer.removeListener(channels.SECURITY_STOP_VALIDATE_ORIGIN, sender)
        }
      },
    },
    powerManagement: {
      getAppCrash: (): false | number => ipcRenderer.sendSync(channels.POWER_MANAGEMENT_APP_CRASH),
      getExamsCrashed: (): Promise<Record<string, number>> =>
        ipcRenderer.invoke(channels.POWER_MANAGEMENT_EXAMS_CRASH),
      setAppCrash: (): void => ipcRenderer.send(channels.POWER_MANAGEMENT_UPDATE_APP_CRASH),
      lockExam: (userId: User["id"], examId: Exam["id"]): Promise<void> =>
        ipcRenderer.invoke(channels.POWER_MANAGEMENT_LOCK_EXAM, userId, examId),
      stopLockExamInterval: () =>
        ipcRenderer.send(channels.POWER_MANAGEMENT_STOP_LOCK_EXAM_INTERVAL),
      unlockExam: (userId: User["id"], examId: Exam["id"]): Promise<void> =>
        ipcRenderer.invoke(channels.POWER_MANAGEMENT_UNLOCK_EXAM, userId, examId),
      activatePreventSleepMode: (): void => ipcRenderer.send(channels.PREVENT_SLEEP_MODE_ACTIVATE),
      deactivatePreventSleepMode: (): void =>
        ipcRenderer.send(channels.PREVENT_SLEEP_MODE_DEACTIVATE),
      onResume: (func: () => void) => {
        ipcRenderer.on(channels.POWER_MANAGEMENT_RESUME, func)
        return () => ipcRenderer.removeListener(channels.POWER_MANAGEMENT_RESUME, func)
      },
      onSuspend: (func: () => void) => {
        ipcRenderer.on(channels.POWER_MANAGEMENT_SUSPEND, func)
        return () => ipcRenderer.removeListener(channels.POWER_MANAGEMENT_SUSPEND, func)
      },
      onLockScreen: (func: () => void) => {
        ipcRenderer.on(channels.POWER_MANAGEMENT_LOCK_SCREEN, func)
        return () => ipcRenderer.removeListener(channels.POWER_MANAGEMENT_LOCK_SCREEN, func)
      },
      onUnlockScreen: (func: () => void) => {
        ipcRenderer.on(channels.POWER_MANAGEMENT_UNLOCK_SCREEN, func)
        return () => ipcRenderer.removeListener(channels.POWER_MANAGEMENT_UNLOCK_SCREEN, func)
      },
    },
    security: {
      askForPrivileges: (): void => ipcRenderer.send(channels.SECURITY_ASK_FOR_PRIVILEGES),
      hasPrivileges: (): boolean => ipcRenderer.sendSync(channels.SECURITY_HAS_PRIVILEGES),
      clearClipboard: (allowCopyPast: boolean): boolean =>
        ipcRenderer.sendSync(channels.SECURITY_CLEAR_CLIPBOARD, allowCopyPast),
      screens: {
        onChanged: (handler: (payload: DisplayMonitorChangedPayload) => void) => {
          const listener = (_event: unknown, payload: DisplayMonitorChangedPayload) => {
            handler(payload)
          }
          ipcRenderer.on(channels.DISPLAY_MONITOR_CHANGED, listener)
          return () => ipcRenderer.removeListener(channels.DISPLAY_MONITOR_CHANGED, listener)
        },
        getScreenCount: (): Promise<number> =>
          ipcRenderer.invoke(channels.DISPLAY_MONITOR_GET_COUNT),
      },
    },
  }

  contextBridge.exposeInMainWorld("electron", electronHandler)
  contextBridge.exposeInMainWorld("process", process)
  contextBridge.exposeInMainWorld(
    "windowName",
    process.argv.find((arg) => arg.startsWith("--window-name="))?.split("=")?.[1] || undefined,
  )

  return electronHandler
}
